// src/sim/characters/profiles.ts — P5 Plan 08 final state (Pitfall 1 cleanup).
//
// The hardcoded PROFILES record has been deleted. Character stats live in
// the Cloudflare-KV-backed ContentBundle (or the build-baked DEFAULT_BUNDLE
// fallback), hydrated into src/sim/content/cache.ts at App boot time
// (P5 Plan 06). This module exports getProfile / hasProfile / listProfileIds
// / DEFAULT_CHARACTER_ID — every pre-P5 consumer (DevOverlay, GameScene,
// LocalInputController, StaminaBar, OnFloorDropButton, AIController) keeps
// the original signature and reads through the cache.
//
// History:
//   - P3: hardcoded PROFILES record (initial stats).
//   - P4 Plan 06: tuned stats in PROFILES.
//   - P5 Plan 01: DEFAULT_BUNDLE mirrored PROFILES byte-for-byte
//     (Pitfall 8 parity guard in src/shared/content/__tests__/default-bundle.test.ts).
//   - P5 Plan 02: getProfile / hasProfile body-swapped to read from
//     src/sim/content/cache.ts; PROFILES kept as the parity source.
//   - P5 Plan 08 (this file): PROFILES + parity test retired. DEFAULT_BUNDLE
//     is now the single source of truth for first-boot / offline content.
//
// Source: .planning/phases/05-content-pipeline-characters-admin-cms/05-CONTEXT.md D-22/D-23;
//         05-RESEARCH.md Pitfall 1 (retire parity guard in the final P5 plan).

import { getContentBundle } from '../content/cache';

import type { CharacterProfile } from './character-profile';

/**
 * Look up a character profile by id. Throws on unknown id — the caller is
 * expected to only ever pass characterIds that round-trip through the
 * hydrated ContentBundle (DEV-overlay picker, CharacterSelect, or
 * DEFAULT_CHARACTER_ID).
 *
 * Reads from src/sim/content/cache.ts. The cache is populated exactly once
 * at App boot (Plan 06 useContent hook); getProfile() called before
 * hydration throws `Content not hydrated.` from getContentBundle().
 */
export function getProfile(id: string): CharacterProfile {
  const bundle = getContentBundle();
  const c = bundle.characters.find((ch) => ch.id === id);
  if (!c) throw new Error(`Unknown character profile id: ${id}`);
  // Strip authoring-only fields (inGameName, quirk, bio, portraitId, unlocked)
  // — Character is a strict superset of CharacterProfile per D-01.
  return {
    id: c.id,
    size: c.size,
    hideSkill: c.hideSkill,
    abilities: c.abilities,
    baseSpeedMultiplier: c.baseSpeedMultiplier,
    sprintMultiplier: c.sprintMultiplier,
    sprintDrainRate: c.sprintDrainRate,
    staminaRegenMoving: c.staminaRegenMoving,
    staminaRegenIdle: c.staminaRegenIdle,
    fear: c.fear,
    see: c.see,
    hear: c.hear,
    // P6 D-06 / D-09 additions — schema -> profile mirror.
    voicePitch: c.voicePitch,
    move: c.move,
    hide: c.hide,
  };
}

/**
 * Whether `id` is a known character profile. Useful for UI gating without
 * the throw. Mount site must run after hydrateContent (Plan 06).
 */
export function hasProfile(id: string): boolean {
  const bundle = getContentBundle();
  return bundle.characters.some((c) => c.id === id);
}

/**
 * Enumerate every known character id. Used by GameScene.ts (roster
 * composition) and DevOverlay.tsx (picker grid). Replaces the P3/P4
 * `Object.keys(PROFILES)` pattern.
 */
export function listProfileIds(): string[] {
  return getContentBundle().characters.map((c) => c.id);
}

/** P3 D-27 — non-DEV fallback character when no dev-overlay pick is set. */
export const DEFAULT_CHARACTER_ID = 'charlie';
